import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import type { Extensions } from '@tiptap/core';
import type { CmsPageKind, TipTapDoc } from '../dataProvider';
import { Cta } from './ctaNode';

/**
 * Admin-side helpers for the conventional rich-text CMS (D-cms-pages-core): the page
 * kinds, the page-id / slug convention, the FOCUSED TipTap extension set (the prose
 * feature set plus ONE cta node, and NOTHING more) and the empty body. The extension
 * set is the SCHEMA: only these nodes / marks are valid, so an unsupported node (a
 * table, a column, an embed) cannot be authored. Shared by the editor and the tests.
 */

/** The light grouping field (NOT a content-shape switch). */
export const CMS_PAGE_KINDS: readonly CmsPageKind[] = [
  'legal',
  'marketing',
  'help',
];

export const KIND_LABEL: Record<CmsPageKind, string> = {
  legal: 'Legal',
  marketing: 'Marketing',
  help: 'Help',
};

/** A slug is lowercase words joined by hyphens (a stable url segment). */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * THE FOCUSED FEATURE SET. StarterKit restricted to the prose set (headings h2 / h3,
 * bold, italic, links, bullet and ordered lists), with the off-scope nodes / marks
 * disabled (no blockquote, code, codeBlock, strike, underline, horizontalRule), plus
 * an image node and the custom cta node. There are NO tables, columns or embeds (the
 * schema editor's job); the TipTap schema enforces this, so a disallowed node round-
 * trips to nothing.
 */
export const cmsExtensions: Extensions = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    blockquote: false,
    code: false,
    codeBlock: false,
    strike: false,
    underline: false,
    horizontalRule: false,
    link: { openOnClick: false, autolink: false },
  }),
  Image,
  Cta,
];

/** An empty CMS body (a doc with one empty paragraph, the ProseMirror empty shape). */
export const emptyDoc = (): TipTapDoc => ({
  type: 'doc',
  content: [{ type: 'paragraph' }],
});

/** Does the body have any real content (more than a single empty paragraph)? */
export const docIsEmpty = (doc: TipTapDoc | undefined): boolean => {
  if (!doc || !doc.content || doc.content.length === 0) return true;
  if (doc.content.length === 1) {
    const only = doc.content[0];
    return only.type === 'paragraph' && !only.content;
  }
  return false;
};
